import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, X, Plus } from "lucide-react";
import { Header } from "@/components/header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { organizations } from "@/organization/api";
import { useQuery } from "@tanstack/react-query";
import { get, type PaginatedResponse } from "@/api/api";
import type { LocationData } from "@/location/location";
import {
  useEmailTemplates,
  useEmailConciergePreview,
  useSendConciergeEmails,
  type PreviewRecipient,
  type SendResult,
} from "@/email-concierge/api";

export const Route = createFileRoute(
  "/_authenticated/_staff/email-concierge"
)({
  component: EmailConciergePage,
});

function EmailConciergePage() {
  const [templateKey, setTemplateKey] = useState("");
  const [orgId, setOrgId] = useState<number | null>(null);
  const [locationId, setLocationId] = useState<string>("");
  const [dashboardUrl, setDashboardUrl] = useState("");
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // Recipient management — local state derived from preview, editable by user
  const [includedRecipients, setIncludedRecipients] = useState<
    PreviewRecipient[]
  >([]);
  const [excludedRecipients, setExcludedRecipients] = useState<
    PreviewRecipient[]
  >([]);

  // Variable overrides — tracks user edits to template variables
  const [variableOverrides, setVariableOverrides] = useState<
    Record<string, string>
  >({});

  const { data: templates } = useEmailTemplates();
  const { data: orgsData } = useQuery(organizations.listMinimal());

  // Only fetch locations after an org is selected
  const { data: locsData, isLoading: locsLoading } = useQuery({
    queryKey: ["emailConcierge", "locations", orgId],
    queryFn: async () => {
      return await get<PaginatedResponse<LocationData>>(
        `organizations/${orgId}/locations/?page=1&page_size=200`
      );
    },
    enabled: orgId !== null,
  });

  const orgs = orgsData?.results ?? [];
  const orgLocations = locsData?.results ?? [];

  // Always target a specific location
  const targetType = locationId ? "location" : "";
  const targetId = locationId ? Number(locationId) : null;

  const preview = useEmailConciergePreview({
    template: templateKey,
    targetType,
    targetId,
    dashboardUrl: dashboardUrl || undefined,
  });

  // Sync local recipient state and reset variable overrides when preview data changes
  useEffect(() => {
    if (preview.data) {
      setIncludedRecipients(preview.data.included);
      setExcludedRecipients(preview.data.excluded);
      setVariableOverrides({});
    }
  }, [preview.data]);

  const sendMutation = useSendConciergeEmails();

  const handleOrgChange = (value: string) => {
    setOrgId(Number(value));
    setLocationId("");
    setSendResult(null);
  };

  const handleExclude = (recipient: PreviewRecipient) => {
    setIncludedRecipients((prev) => prev.filter((r) => r.id !== recipient.id));
    setExcludedRecipients((prev) => [...prev, recipient]);
  };

  const handleInclude = (recipient: PreviewRecipient) => {
    setExcludedRecipients((prev) => prev.filter((r) => r.id !== recipient.id));
    setIncludedRecipients((prev) => [...prev, recipient]);
  };

  const handleSend = () => {
    if (!templateKey || !targetType || targetId === null) return;
    if (includedRecipients.length === 0) return;
    setSendResult(null);
    // Only send overrides for values the user actually changed
    const overrides: Record<string, string> = {};
    if (preview.data?.locations[0]) {
      const defaults = preview.data.locations[0].variables;
      for (const [key, val] of Object.entries(variableOverrides)) {
        if (val !== defaults[key]) {
          overrides[key] = val;
        }
      }
    }

    sendMutation.mutate(
      {
        template: templateKey,
        target_type: targetType,
        target_id: targetId,
        recipient_emails: includedRecipients.map((r) => r.email),
        ...(dashboardUrl ? { dashboard_url: dashboardUrl } : {}),
        ...(Object.keys(overrides).length > 0
          ? { variable_overrides: overrides }
          : {}),
      },
      {
        onSuccess: (data) => setSendResult(data),
      }
    );
  };

  const canPreview = !!templateKey && !!targetType && targetId !== null;
  const canSend =
    canPreview && includedRecipients.length > 0 && !sendMutation.isPending;

  const totalEmails = preview.data
    ? (preview.data.locations.length) * includedRecipients.length
    : 0;

  return (
    <div className="space-y-6">
      <Header
        name="Email Concierge"
        showDatePicker={false}
        showChartOptions={false}
      />

      <Card className="p-6 space-y-5">
        {/* Email Template */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Email Type</label>
          <Select
            value={templateKey}
            onValueChange={(v) => {
              setTemplateKey(v);
              setSendResult(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select email type" />
            </SelectTrigger>
            <SelectContent>
              {(templates ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Organization */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Organization</label>
          <Select
            value={orgId !== null ? String(orgId) : ""}
            onValueChange={handleOrgChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((org) => (
                <SelectItem key={org.id} value={String(org.id)}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location (cascading — shown after org is selected) */}
        {orgId !== null && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Location</label>
            <Select
              value={locationId}
              onValueChange={(v) => {
                setLocationId(v);
                setSendResult(null);
              }}
              disabled={locsLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    locsLoading ? "Loading locations..." : "Select location"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {orgLocations.map((loc) => (
                  <SelectItem key={loc.id} value={String(loc.id)}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Dashboard URL Override */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Dashboard URL{" "}
            <span className="text-muted-foreground font-normal">
              (optional override)
            </span>
          </label>
          <Input
            placeholder="https://copilot.thalolabs.com"
            value={dashboardUrl}
            onChange={(e) => setDashboardUrl(e.target.value)}
          />
        </div>
      </Card>

      {/* Loading Preview */}
      {canPreview && preview.isLoading && (
        <Card className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">
            Loading preview...
          </span>
        </Card>
      )}

      {/* Preview Section */}
      {canPreview && preview.data && (
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Preview</h3>

          <p className="text-sm text-muted-foreground">
            Organization: {preview.data.organization.name}
          </p>

          {/* Template Variables (editable) */}
          {preview.data.locations[0] && (
            <div>
              <h4 className="text-sm font-medium mb-2">
                Template Variables
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(preview.data.locations[0].variables).map(
                  ([key, defaultVal]) => (
                    <div key={key} className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        {key}
                      </label>
                      <Input
                        value={variableOverrides[key] ?? defaultVal}
                        onChange={(e) =>
                          setVariableOverrides((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Recipients — included */}
          <div>
            <h4 className="text-sm font-medium mb-2">
              Recipients ({includedRecipients.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {includedRecipients.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => handleExclude(r)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                >
                  {r.first_name} {r.last_name} &lt;{r.email}&gt;
                  {r.is_staff && (
                    <span className="text-[10px] opacity-60">(staff)</span>
                  )}
                  <X className="h-3 w-3 ml-0.5" />
                </button>
              ))}
              {includedRecipients.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  No recipients selected
                </span>
              )}
            </div>
          </div>

          {/* Recipients — excluded */}
          {excludedRecipients.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">
                Excluded ({excludedRecipients.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {excludedRecipients.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => handleInclude(r)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                  >
                    {r.first_name} {r.last_name} &lt;{r.email}&gt;
                    {r.is_staff && (
                      <span className="text-[10px] opacity-60">(staff)</span>
                    )}
                    <Plus className="h-3 w-3 ml-0.5" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Summary line */}
          <p className="text-sm text-muted-foreground">
            This will send{" "}
            <strong>{totalEmails}</strong>{" "}
            email{totalEmails !== 1 ? "s" : ""}{" "}
            ({preview.data.locations.length} location
            {preview.data.locations.length !== 1 ? "s" : ""} &times;{" "}
            {includedRecipients.length} recipient
            {includedRecipients.length !== 1 ? "s" : ""})
          </p>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="w-full"
            style={{ backgroundColor: "#000F89" }}
          >
            {sendMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Emails"
            )}
          </Button>
        </Card>
      )}

      {/* Success State */}
      {sendResult && (
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Emails Sent</h3>
          </div>
          <p className="text-sm">
            <strong>{sendResult.sent}</strong> sent
            {sendResult.failed > 0 && (
              <>
                ,{" "}
                <strong className="text-red-600">{sendResult.failed}</strong>{" "}
                failed
              </>
            )}
          </p>
          <div className="overflow-auto rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Location</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {sendResult.recipients.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{r.email}</td>
                    <td className="px-3 py-2">{r.location}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          r.status === "sent"
                            ? "text-green-700"
                            : "text-red-600"
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
